import { supabase } from './supabase';
import { createResourceWorkingAvailability } from './resourceAvailability';

interface ResourceShift {
  start: string;
  end: string;
}

interface DaySchedule {
  enabled: boolean;
  shifts: ResourceShift[];
}

interface ResourceSchedule {
  monday: DaySchedule;
  tuesday: DaySchedule;
  wednesday: DaySchedule;
  thursday: DaySchedule;
  friday: DaySchedule;
  saturday: DaySchedule;
  sunday: DaySchedule;
}

export interface Resource {
  id: string;
  business_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  role: string; // Legacy role column (kept for database compatibility)
  role_id?: string; // Permission role ID
  roleName?: string; // Role name from new permission system
  permission_role?: { // Permission role details
    id: string;
    name: string;
    description: string;
    permissions: any;
  };
  hourly_rate: number;
  commission_rate: number;
  status: string;
  last_active?: string; // Made optional since column doesn't exist in database
  created_at: string;
  updated_at: string;
  permissions?: ResourcePermission[];
  schedule_availability: ResourceSchedule;
  compensation_type: 'hourly' | 'flat_rate' | 'commission';
  flat_rate_amount: number;
  commission_percentage: number;
  earnings_ytd: number;
  earnings_mtd: number;
  earnings_last_month: number;
  is_mobile_available: boolean;
  auth_user_id?: string;
  is_admin: boolean;
  password?: string; // Temporary field for database triggers
  avatar_url?: string;
  bonus_configuration?: {
    booking_bonus?: {
      enabled: boolean;
      percentage: number;
    };
    // Future bonus types can be added here
  };
}

type CreateResourcePayload = Omit<
  Resource,
  'id' | 'created_at' | 'updated_at' | 'permissions'
> & {
  password: string;
  confirm_password: string;
};

interface ResourcePermission {
  id: string;
  resource_id: string;
  permission_type: string;
  permission_action: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export async function getResources(businessId: string) {
  if (!supabase) {
    throw new Error('Supabase client is not initialized');
  }

  console.log('Fetching resources for business:', businessId);

  const { data, error } = await supabase
    .from('resources')
    .select(
      `
      *,
      permissions:resource_permissions(*),
      permission_role:resource_roles(*)
    `
    )
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching resources:', error);
    throw error;
  }

  console.log('Resources fetched:', data);
  return data as Resource[];
}

export async function getResourceServiceAreas(resourceId: string, day: string) {
  const { data, error } = await supabase
    .from('resource_service_areas')
    .select('service_area_id')
    .eq('resource_id', resourceId)
    .eq('day_of_week', day);

  if (error) throw error;
  return data;
}

async function getResource(id: string) {
  const { data, error } = await supabase
    .from('resources')
    .select(
      `
      *,
      permissions:resource_permissions(*)
    `
    )
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as Resource;
}

export async function getResourceByAuthId(authUserId: string) {
  if (!authUserId) {
    console.log('No auth user ID provided');
    return null;
  }

  const { data, error } = await supabase
    .from('resources')
    .select('*')
    .eq('auth_user_id', authUserId)
    .maybeSingle();

  if (error) {
    // Only log as error if it's not a "no rows returned" error
    if (!error.message.includes('no rows returned')) {
      console.error('Error getting resource by auth ID:', {
        error,
        authUserId
      });
    } else {
      console.log('No resource found for auth ID:', authUserId);
    }
    return null;
  }

  if (!data) {
    console.log('No resource found for auth ID:', authUserId);
    return null;
  }

  return data as Resource | null;
}

export async function createResource(resource: CreateResourcePayload) {
  console.log('🔍 [DEBUG] createResource called with payload:', resource);
  
  if (!supabase) {
    console.error('❌ [DEBUG] Supabase client is not initialized');
    throw new Error('Supabase client is not initialized');
  }

  const { confirm_password, password, ...resourceData } = resource;
  console.log('🔍 [DEBUG] Extracted resource data (password removed temporarily):', resourceData);

  // Add default schedule availability
  const defaultSchedule: ResourceSchedule = {
    monday: { enabled: true, shifts: [{ start: '09:00', end: '17:00' }] },
    tuesday: { enabled: true, shifts: [{ start: '09:00', end: '17:00' }] },
    wednesday: { enabled: true, shifts: [{ start: '09:00', end: '17:00' }] },
    thursday: { enabled: true, shifts: [{ start: '09:00', end: '17:00' }] },
    friday: { enabled: true, shifts: [{ start: '09:00', end: '17:00' }] },
    saturday: { enabled: false, shifts: [] },
    sunday: { enabled: false, shifts: [] }
  };

  resourceData.schedule_availability = defaultSchedule;
  console.log('🔍 [DEBUG] Added default schedule availability');

  // Password temporarily removed due to schema cache issue
  console.log('🔍 [DEBUG] Password temporarily removed from insert to fix schema cache issue');

  // Set admin status (defaults to false if not specified)
  resourceData.is_admin = resource.is_admin ?? false;
  console.log('🔍 [DEBUG] Set admin status:', resourceData.is_admin);

  // Initialize earnings fields
  resourceData.earnings_ytd = 0;
  resourceData.earnings_mtd = 0;
  resourceData.earnings_last_month = 0;
  resourceData.is_mobile_available = false;
  console.log('🔍 [DEBUG] Initialized earnings fields');

  console.log('🔍 [DEBUG] About to insert resource into database with data:', resourceData);

  // Insert the resource data into the 'resources' table
  const { data: newResource, error } = await supabase
    .from('resources')
    .insert([resourceData])
    .select(
      `
      *,
      permissions:resource_permissions(*)
    `
    )
    .single();

  console.log('🔍 [DEBUG] Database insert result:', { data: newResource, error });

  // Check if there was an error inserting the resource
  if (error || !newResource) {
    console.error('❌ [DEBUG] Error inserting resource:', error);
    console.error('❌ [DEBUG] Error details:', {
      code: error?.code,
      message: error?.message,
      details: error?.details,
      hint: error?.hint
    });
    throw error || new Error('Resource creation failed without data.');
  }

  console.log('✅ [DEBUG] Resource created successfully:', newResource);

  // Create working availability record
  try {
    console.log('🔍 [DEBUG] Creating resource working availability...');
    await createResourceWorkingAvailability(newResource.id, newResource.business_id, {
      monday: defaultSchedule.monday,
      tuesday: defaultSchedule.tuesday,
      wednesday: defaultSchedule.wednesday,
      thursday: defaultSchedule.thursday,
      friday: defaultSchedule.friday,
      saturday: defaultSchedule.saturday,
      sunday: defaultSchedule.sunday
    });
    console.log('✅ [DEBUG] Resource working availability created successfully');
  } catch (availabilityError) {
    console.error('❌ [DEBUG] Error creating resource working availability:', availabilityError);
    // Delete the created resource since availability creation failed
    console.log('🔍 [DEBUG] Deleting resource due to availability creation failure...');
    if (supabase) {
      await supabase.from('resources').delete().eq('id', newResource.id);
    }
    throw availabilityError;
  }

  console.log('✅ [DEBUG] createResource completed successfully, returning:', newResource);
  // Return the created resource, ensuring the type is correct
  return newResource as Resource;
}

export async function updateResource(id: string, updates: Partial<Resource>) {
  const { permissions, ...resourceUpdates } = updates;

  const { data, error } = await supabase
    .from('resources')
    .update(resourceUpdates)
    .eq('id', id)
    .select(
      `
      *,
      permissions:resource_permissions(*)
    `
    )
    .single();

  if (error) throw error;

  if (permissions) {
    // First delete existing permissions
    const { error: deleteError } = await supabase
      .from('resource_permissions')
      .delete()
      .eq('resource_id', id);

    if (deleteError) throw deleteError;

    // Then insert new permissions if any exist
    if (permissions.length > 0) {
      const { error: insertError } = await supabase
        .from('resource_permissions')
        .insert(
          permissions.map((p) => ({
            resource_id: id,
            permission_type: p.permission_type,
            permission_action: p.permission_action,
            enabled: p.enabled,
          }))
        );

      if (insertError) throw insertError;
    }
  }

  return data as Resource;
}

export async function activateResource(id: string): Promise<Resource> {
  if (!supabase) {
    throw new Error('Supabase client is not initialized');
  }

  const { data, error } = await supabase
    .from('resources')
    .update({ status: 'active' })
    .eq('id', id)
    .select(
      `
      *,
      permissions:resource_permissions(*)
    `
    )
    .single();

  if (error) throw error;
  return data as Resource;
}

export async function deleteResource(id: string) {
  const { error } = await supabase.from('resources').delete().eq('id', id);

  if (error) throw error;
}

async function updateResourcePermissions(
  resourceId: string,
  permissions: Partial<ResourcePermission>[]
) {
  const { error } = await supabase.from('resource_permissions').upsert(
    permissions.map((p) => ({
      resource_id: resourceId,
      permission_type: p.permission_type,
      permission_action: p.permission_action,
      enabled: p.enabled,
    }))
  );

  if (error) throw error;
}
